import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from "react-router-dom";
import moment from 'moment-timezone';
import axios from "axios";
import {IonAlert, IonCol, IonLoading, IonRow} from "@ionic/react";
import {StyledAccordion, StyledButton, StyledButtonOption, StyledOptions, StyledTravelAllowanceContent} from '@app/travelAllowance/travelAllowance.style';
import {StyledEmptyContainer} from "@components/content/content.style";
import {ReactComponent as AddIcon} from '@assets/images/travelAllowance/add.svg';
import {ReactComponent as RideIcon} from '@assets/images/travelAllowance/ride.svg';
import {Ride} from "@models/travelAllowance/ride";
import {getRides, removeRide} from "@services/travelAllowance/ride.service";
import MyDataSkeleton from "@app/myData/components/myDataSkeleton.component";
import RideListRow from "@app/travelAllowance/ride/components/rideListRow.component";
import CircleImage, {ImageTypes} from "@components/circleImage/circleImage.component";
import {Links} from "@app/links";
import {ReactComponent as ArrowLeftIcon} from "@assets/images/travelAllowance/arrow-left.svg"
import {ReactComponent as ArrowRightIcon} from "@assets/images/travelAllowance/arrow-right.svg"
import {RideStatus} from "@enums/travelAllowance/ride";
import StaticPane from "@components/pane/static-pane.component";
import useOffsetTop from "@hooks/useOffsetTop";
import RideDetailsModal from "@app/travelAllowance/ride/modals/rideDetailsModal.component";
import MissingReportNoticeModal from "@app/travelAllowance/ride/modals/missingReportNoticeModal.component";
import useMissingReportNotices from "@app/travelAllowance/ride/hooks/useMissingReportNotices";
import {getServerNow} from "@services/serverDate.service";

type RideListPaneProps = {
    topEdge?: number;
}

type SelectedDate = {
    basicFormat: {
        month: string,
        year: string,
    },
    apiFormat: string
}

const RideListPane: React.FC<RideListPaneProps> = ({topEdge}: RideListPaneProps) => {
    const cancelToken = axios.CancelToken.source();

    const {t} = useTranslation();
    const history = useHistory();
    const {ref: boxRideRef, offsetTop} = useOffsetTop<HTMLDivElement>();
    const missingReportNotices = useMissingReportNotices();

    const [loading, setLoading] = useState<boolean>(false);
    const [rides, setRides] = useState<Ride[]>([]);
    const [selectedDate, setSelectedDate] = useState<SelectedDate>();
    const [dateCounter, setDateCounter] = useState<number>(0);

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [selectedRide, setSelectedRide] = useState<Ride>();
    const [rideToRemove, setRideToRemove] = useState<Ride>();
    const [showRemoveRideAlert, setShowRemoveRideAlert] = useState<boolean>(false);

    useEffect(() => {
        setSelectedDate(getDate);
    }, [dateCounter]);

    useEffect(() => {
        setLoading(true);

        fetchRides()
            .then(() => {
                setLoading(false);
            })
            .catch((e) => {
                console.error(e);
            });

        missingReportNotices.refresh();

        return () => {
            cancelToken.cancel();
        };
    }, [selectedDate, history.location.pathname]);

    const fetchRides = async () => {
        if (selectedDate?.apiFormat) {
            const ridesData = await getRides(selectedDate.apiFormat, cancelToken);
            ridesData.sort(sortByWarningStatus);

            setRides(ridesData);
        }
    }

    const getDate = (): SelectedDate => {
        return {
            basicFormat: {
                month: getServerNow().locale('en').subtract(dateCounter, 'months').format('MMMM').toLowerCase(),
                year: getServerNow().subtract(dateCounter, 'months').format('YYYY'),
            },
            apiFormat: getServerNow().subtract(dateCounter, 'months').format('YYYY-MM-DD'),
        };
    }

    const sortByWarningStatus = (a: Ride, b: Ride) => {
        if (a.status === RideStatus.QUARANTINE && b.status !== RideStatus.QUARANTINE) {
            return -1;
        } else if (a.status !== RideStatus.QUARANTINE && b.status === RideStatus.QUARANTINE) {
            return 1;
        } else {
            if (a.id > b.id) {
                return -1;
            } else if (a.id < b.id) {
                return 1;
            } else {
                return 0;
            }
        }
    }

    const handleRideRemove = async () => {
        if (!rideToRemove) return;

        setRideToRemove(undefined);
        setShowRemoveRideAlert(false);

        try {
            setShowLoader(true);
            await removeRide(rideToRemove.id);
            setShowLoader(false);

            setLoading(true);
            await fetchRides();
            setLoading(false);
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <StaticPane topEdge={topEdge} marginTop={40} paddingTop={30} hideGrabber={true}>
            {
                <StyledTravelAllowanceContent>
                    <IonAlert
                        isOpen={showRemoveRideAlert}
                        onDidDismiss={() => {
                            setRideToRemove(undefined);
                            setShowRemoveRideAlert(false);
                        }}
                        header={t('common.alertHeader')}
                        buttons={[
                            {
                                text: t('common.alertCancel'),
                                role: 'cancel',
                                cssClass: 'secondary',
                                handler: () => {
                                    setRideToRemove(undefined);
                                    setShowRemoveRideAlert(false);
                                },
                            },
                            {
                                text: t('common.alertConfirm'),
                                handler: async () => {
                                    await handleRideRemove();
                                }
                            }
                        ]}
                    />
                    <IonLoading onDidDismiss={() => setShowLoader(false)} isOpen={showLoader}/>
                    <RideDetailsModal isOpen={!!selectedRide} onClose={() => setSelectedRide(undefined)} ride={selectedRide}/>
                    <MissingReportNoticeModal notice={missingReportNotices.currentNotice}
                                              onClose={() => missingReportNotices.acknowledgeCurrent()}
                                              onDismissed={() => missingReportNotices.showNext()}/>
                    <StyledAccordion>
                        <IonRow className="row">
                            <IonCol style={{padding: 0, margin: 0}}
                                    className="title">
                                {t(`common.month.${selectedDate?.basicFormat.month}`)}&nbsp;{selectedDate?.basicFormat.year}
                            </IonCol>
                            <IonCol style={{display: "flex", justifyContent: "end", gap: "5px"}}>
                                <div className="arrow" onClick={() => setDateCounter(dateCounter + 1)}>
                                    <ArrowLeftIcon/>
                                </div>
                                <div className="arrow" onClick={() => setDateCounter(dateCounter + -1)}>
                                    <ArrowRightIcon/>
                                </div>
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <hr/>
                        </IonRow>
                    </StyledAccordion>
                    <div className="travel-division">
                        <section ref={boxRideRef} className="ride-box" style={{height: `calc((100vh - ${offsetTop}px) - 210px - env(safe-area-inset-bottom, 0px))`}}>
                            {
                                loading && <MyDataSkeleton></MyDataSkeleton>
                            }
                            {
                                !loading && rides.length > 0 && rides.map((ride) => {
                                    return (
                                        <RideListRow
                                            key={ride.id}
                                            ride={ride}
                                            handleRideDetailsPreview={setSelectedRide}
                                            handleRideRemove={() => {
                                                setShowRemoveRideAlert(true);
                                                setRideToRemove(ride);
                                            }}/>
                                    )
                                })
                            }
                            {
                                !loading && rides.length === 0 &&
                                <StyledEmptyContainer>
                                    <CircleImage image={ImageTypes.T} color="grey"/>
                                    <h3>{t('travelAllowance.ride.empty')}</h3>
                                </StyledEmptyContainer>
                            }
                        </section>
                        <section className="button-box">
                            <StyledButton onClick={() => history.push(Links.main + Links.travelAllowance.ride.add)}>
                                <div className="btn center">
                                    <span>
                                        <RideIcon/>
                                    </span>
                                    <span>{t('travelAllowance.ride.add')}</span>
                                </div>
                            </StyledButton>
                            <StyledOptions>
                                <StyledButtonOption
                                    onClick={() => history.push(Links.main + Links.travelAllowance.car.add)}>
                                    <div className="btn-option center">
                                        <AddIcon/>
                                        <span>{t('travelAllowance.car.add')}</span>
                                    </div>
                                </StyledButtonOption>
                                <StyledButtonOption
                                    onClick={() => history.push(Links.main + Links.travelAllowance.rideRoute.add)}>
                                    <div className="btn-option center">
                                        <AddIcon/>
                                        <span>{t('travelAllowance.rideRoute.add')}</span>
                                    </div>
                                </StyledButtonOption>
                            </StyledOptions>
                        </section>
                    </div>
                </StyledTravelAllowanceContent>
            }
        </StaticPane>
    );
};

export default RideListPane;